import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Smartphone,
  Tag,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';
import { 
  DispositivoSsem,
  DispositivoSsemFormData, 
  DispositivoSsemValidationErrors,
  TagDisponible 
} from '../../interfaces/DispositivoSsemInterface';
import { 
  getDispositivoSsem,
  updateDispositivoSsem, 
  getTagsDisponiblesParaEditar 
} from '../../apis/dispositivoSsemApi';

export const EditarDispositivo: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [tagsDisponibles, setTagsDisponibles] = useState<TagDisponible[]>([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [errors, setErrors] = useState<DispositivoSsemValidationErrors>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [dispositivo, setDispositivo] = useState<DispositivoSsem | null>(null);

  const [formData, setFormData] = useState<DispositivoSsemFormData>({
    imei_ssem: '',
    marca_ssem: '',
    modelo_ssem: '',
    color_ssem: '',
    capacidad_almacenamiento_ssem: '',
    ram_ssem: '',
    estatus_ssem: 'activo',
    tagblac_id: null,
    observaciones: ''
  });

  useEffect(() => {
    if (id) {
      loadDispositivo(parseInt(id));
    }
  }, [id]);

  const loadDispositivo = async (dispositivoId: number) => {
    try {
      setLoadingData(true);
      const response = await getDispositivoSsem(dispositivoId);
      const dispositivo = response.data;
      
      setDispositivo(dispositivo);
      
      // Cargar datos en el formulario
      setFormData({
        imei_ssem: dispositivo.imei_ssem,
        marca_ssem: dispositivo.marca_ssem,
        modelo_ssem: dispositivo.modelo_ssem,
        color_ssem: dispositivo.color_ssem,
        capacidad_almacenamiento_ssem: dispositivo.capacidad_almacenamiento_ssem,
        ram_ssem: dispositivo.ram_ssem,
        estatus_ssem: dispositivo.estatus_ssem,
        tagblac_id: dispositivo.tagblac_id || null,
        observaciones: dispositivo.observaciones || ''
      });

      // Cargar tags disponibles para este dispositivo
      await loadTagsDisponibles(dispositivoId);
      
    } catch (error: any) {
      console.error('Error cargando dispositivo:', error);
      setErrors({
        imei_ssem: [error.message || 'Error al cargar el dispositivo']
      });
    } finally {
      setLoadingData(false);
    }
  };

  const loadTagsDisponibles = async (dispositivoId: number) => {
    try {
      setLoadingTags(true);
      const response = await getTagsDisponiblesParaEditar(dispositivoId);
      setTagsDisponibles(response.data);
    } catch (error) {
      console.error('Error cargando tags disponibles:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'tagblac_id' ? (value ? parseInt(value) : null) : value
    }));

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name as keyof DispositivoSsemValidationErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) return;
    
    try {
      setLoading(true);
      setErrors({});
      setSuccessMessage('');

      await updateDispositivoSsem(parseInt(id), formData);
      
      setSuccessMessage('Dispositivo actualizado exitosamente');
      
      // Redirigir después de 2 segundos
      setTimeout(() => {
        navigate(`/dispositivos-ssem/${id}`);
      }, 2000);

    } catch (error: any) {
      console.error('Error actualizando dispositivo:', error);
      
      if (error.errors) {
        setErrors(error.errors);
      } else {
        setErrors({
          imei_ssem: [error.message || 'Error al actualizar dispositivo']
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return 'N/A';
    try {
      return new Date(dateTime).toLocaleString('es-MX');
    } catch {
      return dateTime;
    }
  };

  const styles = `
    .editar-dispositivo-container {
      min-height: 100vh;
      background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
      color: white;
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .content-wrapper {
      max-width: 1000px;
      margin: 0 auto;
      padding: 2rem;
    }

    .header-section {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-bottom: 2rem;
    }

    .back-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      text-decoration: none;
      transition: all 0.3s ease;
    }

    .back-button:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: translateX(-2px);
    }

    .page-title {
      font-size: 2rem;
      font-weight: 700;
      background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .main-content {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 2rem;
    }

    .form-container {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      padding: 2rem;
    }

    .info-sidebar {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      padding: 2rem;
      height: fit-content;
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1.2rem;
      font-weight: 600;
      color: #007AFF;
      margin-bottom: 1.5rem;
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .form-group.full-width {
      grid-column: 1 / -1;
    }

    .form-label {
      font-weight: 500;
      color: rgba(255, 255, 255, 0.9);
      font-size: 0.9rem;
    }

    .form-label.required::after {
      content: ' *';
      color: #EF4444;
    }

    .form-input {
      padding: 12px 16px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .form-input:focus {
      outline: none;
      border-color: #007AFF;
      background: rgba(255, 255, 255, 0.15);
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
    }

    .form-input::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    .form-select {
      padding: 12px 16px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .form-select:focus {
      outline: none;
      border-color: #007AFF;
      background: rgba(255, 255, 255, 0.15);
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
    }

    .form-select option {
      background: #1a1a1a;
      color: white;
    }

    .form-textarea {
      padding: 12px 16px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      font-size: 14px;
      resize: vertical;
      min-height: 100px;
      transition: all 0.3s ease;
    }

    .form-textarea:focus {
      outline: none;
      border-color: #007AFF;
      background: rgba(255, 255, 255, 0.15);
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
    }

    .form-textarea::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    .form-error {
      color: #EF4444;
      font-size: 0.8rem;
      margin-top: 0.25rem;
    }

    .form-help {
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.8rem;
      margin-top: 0.25rem;
    }

    .form-actions {
      display: flex;
      gap: 1rem;
      justify-content: flex-end;
      padding-top: 2rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      text-decoration: none;
    }

    .btn-primary {
      background: #007AFF;
      color: white;
    }

    .btn-primary:hover:not(:disabled) {
      background: #0056CC;
      transform: translateY(-1px);
    }

    .btn-secondary {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.15);
    }

    .btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none !important;
    }

    .success-message {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 1rem;
      background: rgba(16, 185, 129, 0.1);
      border: 1px solid rgba(16, 185, 129, 0.3);
      border-radius: 8px;
      color: #10B981;
      margin-bottom: 1rem;
      animation: slideIn 0.3s ease-out;
    }

    .info-item {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      margin-bottom: 1rem;
    }

    .info-label {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.6);
    }

    .info-value {
      font-weight: 500;
      color: white;
    }

    .tag-current {
      background: rgba(0, 122, 255, 0.1);
      border: 1px solid rgba(0, 122, 255, 0.2);
      border-radius: 8px;
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .tag-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #007AFF;
      font-weight: 500;
      margin-bottom: 0.5rem;
    }

    .loading-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }

    .loading-spinner {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 2rem;
      background: rgba(0, 0, 0, 0.8);
      border-radius: 12px;
      color: white;
    }

    .spinner {
      width: 32px;
      height: 32px;
      border: 3px solid rgba(255, 255, 255, 0.1);
      border-top: 3px solid #007AFF;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (max-width: 768px) {
      .content-wrapper {
        padding: 1rem;
      }

      .main-content {
        grid-template-columns: 1fr;
      }

      .form-grid {
        grid-template-columns: 1fr;
      }

      .form-actions {
        flex-direction: column;
      }
    }
  `;

  if (loadingData) {
    return (
      <>
        <style>{styles}</style>
        <div className="editar-dispositivo-container">
          <div className="content-wrapper">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <div className="loading-spinner">
                <div className="spinner" />
                Cargando dispositivo...
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="editar-dispositivo-container">
        <div className="content-wrapper">
          {/* Header */}
          <div className="header-section">
            <button onClick={() => navigate(`/dispositivos-ssem/${id}`)} className="back-button">
              <ArrowLeft size={20} />
              Volver
            </button>
            <h1 className="page-title">Editar Dispositivo Skyangel</h1>
          </div>

          <div className="main-content">
            {/* Formulario */}
            <div className="form-container">
              <h2 className="section-title">
                <Smartphone size={24} />
                Información del Dispositivo
              </h2>

              {successMessage && (
                <div className="success-message">
                  <CheckCircle size={20} />
                  {successMessage}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  {/* IMEI */}
                  <div className="form-group">
                    <label className="form-label required">IMEI</label>
                    <input
                      type="text"
                      name="imei_ssem"
                      value={formData.imei_ssem}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Ej: 123456789012345"
                      required
                    />
                    {errors.imei_ssem && (
                      <div className="form-error">{errors.imei_ssem[0]}</div>
                    )}
                    <div className="form-help">Identificador único del dispositivo (15 dígitos)</div>
                  </div>

                  {/* Marca */}
                  <div className="form-group">
                    <label className="form-label required">Marca</label>
                    <input
                      type="text"
                      name="marca_ssem"
                      value={formData.marca_ssem}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Ej: Samsung"
                      required
                    />
                    {errors.marca_ssem && (
                      <div className="form-error">{errors.marca_ssem[0]}</div>
                    )}
                  </div>

                  {/* Modelo */}
                  <div className="form-group">
                    <label className="form-label required">Modelo</label>
                    <input
                      type="text"
                      name="modelo_ssem"
                      value={formData.modelo_ssem}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Ej: Galaxy Tab A8"
                      required
                    />
                    {errors.modelo_ssem && (
                      <div className="form-error">{errors.modelo_ssem[0]}</div>
                    )}
                  </div>

                  {/* Color */}
                  <div className="form-group">
                    <label className="form-label required">Color</label>
                    <input
                      type="text"
                      name="color_ssem"
                      value={formData.color_ssem}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Ej: Negro"
                      required
                    />
                    {errors.color_ssem && (
                      <div className="form-error">{errors.color_ssem[0]}</div>
                    )}
                  </div>

                  {/* Capacidad de Almacenamiento */}
                  <div className="form-group">
                    <label className="form-label required">Almacenamiento</label>
                    <input
                      type="text"
                      name="capacidad_almacenamiento_ssem"
                      value={formData.capacidad_almacenamiento_ssem}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Ej: 128GB"
                      required
                    />
                    {errors.capacidad_almacenamiento_ssem && (
                      <div className="form-error">{errors.capacidad_almacenamiento_ssem[0]}</div>
                    )}
                  </div>

                  {/* RAM */}
                  <div className="form-group">
                    <label className="form-label required">Memoria RAM</label>
                    <input
                      type="text"
                      name="ram_ssem"
                      value={formData.ram_ssem}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Ej: 8GB"
                      required
                    />
                    {errors.ram_ssem && (
                      <div className="form-error">{errors.ram_ssem[0]}</div>
                    )}
                  </div>

                  {/* Estado */}
                  <div className="form-group">
                    <label className="form-label required">Estado</label>
                    <select
                      name="estatus_ssem"
                      value={formData.estatus_ssem}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                      <option value="mantenimiento">Mantenimiento</option>
                      <option value="perdido">Perdido</option>
                    </select>
                    {errors.estatus_ssem && (
                      <div className="form-error">{errors.estatus_ssem[0]}</div>
                    )}
                  </div>

                  {/* Tag */}
                  <div className="form-group">
                    <label className="form-label">Tag Asignada</label>
                    <select
                      name="tagblac_id"
                      value={formData.tagblac_id || ''}
                      onChange={handleInputChange}
                      className="form-select"
                      disabled={loadingTags}
                    >
                      <option value="">Sin tag asignada</option>
                      {tagsDisponibles.map(tag => (
                        <option key={tag.id} value={tag.id}>
                          {tag.name_device} ({tag.plate})
                        </option>
                      ))}
                    </select>
                    {errors.tagblac_id && (
                      <div className="form-error">{errors.tagblac_id[0]}</div>
                    )}
                    <div className="form-help">
                      {loadingTags ? 'Cargando tags disponibles...' : 
                       tagsDisponibles.length === 0 ? 'No hay tags disponibles' :
                       'Opcional: Asignar una tag de tracking Blac'}
                    </div>
                  </div>

                  {/* Observaciones */}
                  <div className="form-group full-width">
                    <label className="form-label">Observaciones</label>
                    <textarea
                      name="observaciones"
                      value={formData.observaciones}
                      onChange={handleInputChange}
                      className="form-textarea"
                      placeholder="Observaciones adicionales sobre el dispositivo..."
                    />
                    {errors.observaciones && (
                      <div className="form-error">{errors.observaciones[0]}</div>
                    )}
                  </div>
                </div>

                {/* Acciones */}
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => navigate(`/dispositivos-ssem/${id}`)}
                    className="btn btn-secondary"
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    <Save size={16} />
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </div>
              </form>
            </div>

            {/* Sidebar con información */}
            <div className="info-sidebar">
              <h3 className="section-title">
                <Info size={20} />
                Información Actual
              </h3>

              {dispositivo && (
                <>
                  <div className="info-item">
                    <span className="info-label">ID del Sistema</span>
                    <span className="info-value">#{dispositivo.id}</span>
                  </div>

                  <div className="info-item">
                    <span className="info-label">Fecha de Registro</span>
                    <span className="info-value">{formatDateTime(dispositivo.created_at)}</span>
                  </div>

                  <div className="info-item">
                    <span className="info-label">Última Actualización</span>
                    <span className="info-value">{formatDateTime(dispositivo.updated_at)}</span>
                  </div>

                  {dispositivo.tag_blac && (
                    <div className="tag-current">
                      <div className="tag-header">
                        <Tag size={16} />
                        Tag Actual
                      </div>
                      <div className="info-item">
                        <span className="info-label">Dispositivo</span>
                        <span className="info-value">{dispositivo.tag_blac.name_device}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Nombre</span>
                        <span className="info-value">{dispositivo.tag_blac.plate}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Asignada</span>
                        <span className="info-value">{formatDateTime(dispositivo.fecha_asignacion_tag)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner">
              <div className="spinner" />
              Actualizando dispositivo...
            </div>
          </div>
        )}
      </div>
    </>
  );
};
